'use client';

import { useId } from 'react';
import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiBarChartBoxLine,
  RiFlowChart,
  RiImageLine,
  RiRobot2Line,
  RiPaletteLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Mentorivo ne yapar" — ürünün dört modülü ve ortada tek bir görsel.
 *
 * Sayfadaki yeri: platform/marka şeridinin hemen altı. Oradaki soru "hangi
 * platformlara bağlanıyor" idi, burada cevaplanan soru "peki bu panel benim
 * için ne yapıyor".
 *
 * ── Kartın içeriği ────────────────────────────────────────────────────────
 *
 * Sıra her modülde aynı: ikon (üstte) → ad → açıklama. Eskiden altlarında
 * duran ölçüm blokları (iri rakam, delta, grafik, tablo, dipnot) 12 Ağu
 * 2026'da KALDIRILDI (kullanıcı kararı) — ürünün kendi arayüzü uydurulmuş
 * örnek rakamlardan daha çok şey anlatıyor ve "ÖRNEK" etiketiyle sürekli
 * özür dilemek gerekmiyor.
 *
 * ── Düzen: dört kart, ortada tek görsel ───────────────────────────────────
 *
 * 🔴 12 Ağu 2026 (kullanıcı kararı): madde başına görsel YOK. Bölümün tek bir
 * görseli var ve o da MERKEZDE — solda iki, sağda iki madde, ortada iki satır
 * boyunca uzanan dikey bir panel. Maddelerin kendisi ikon + ad + açıklamadan
 * ibaret; altlarındaki ölçüm blokları (iri rakam, grafik, tablo, dipnot) aynı
 * kararla kaldırıldı.
 *
 * DOM sırası a → b → görsel → c → d, yerleşim `grid-template-areas` ile
 * (globals.css). `order` kullanılmıyor: tek sütuna düşen telefonda görselin
 * ikinci ile üçüncü maddenin arasında kalması zaten istenen sıra, yani okuma
 * sırası ile görsel sıra ayrışmıyor.
 *
 * 🔴 Görsel HENÜZ YOK: `SECTION_SHOT` `null`. Bu hâlde panel sessiz bir yer
 * tutucu (ızgara doku + bir görsel işareti, metin yok). Geldiğinde yapılacak
 * tek şey dosya yolunu yazmak — başka değişiklik gerekmiyor:
 *
 *     const SECTION_SHOT = '/shots/modules.png';
 *
 * Dosya `public/shots/` altına konur. Panelin oranı sabit değil (geniş
 * ekranda boyu maddelerden geliyor), o yüzden görsel `object-fit: cover` ile
 * kırpılır — dikey, ortası kalabalık bir kompozisyon seçin.
 *
 * ── Metin ─────────────────────────────────────────────────────────────────
 *
 * Modül adları header menüsündeki anahtarın TA KENDİSİNDEN okunuyor
 * (`header.menu.<key>.title`), kopyalanmıyor — iki yerde ayrı ayrı durursa
 * er geç ayrışırlar. Geri kalan her şey `capabilities` ad alanında.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * 1. **Giriş yok** — kartlar ve panel sayfa açılır açılmaz son hâllerinde
 *    duruyor (`initial={false}`). Eskiden her biri KENDİ görüş alanı birimiydi
 *    (`whileInView`, `once: true`) ve sayfa kaydırıldıkça açılıyordu; site
 *    sahipleri bunu istemedi (kullanıcı kararı, 9 Eyl 2026). Variant'lar
 *    (`cellV`, `headV`, `shotV`) yerinde duruyor — hover onları kullanmaya
 *    devam ediyor ve giriş geri istenirse tetik tek satır.
 *
 * 2. **Üzerine gelince** — kartta yalnızca RENK değişiyor (kenarlık, ikon,
 *    numara) ve o da CSS'te: yükselme, gölge yok (kullanıcı kararı). Panelde
 *    ise görsel çerçevenin içinde bir tık yakınlaşıyor; bu Motion'ın `hover`
 *    variant'ında, çünkü Motion animasyonladığı öğeye satır içi `transform`
 *    bırakıyor ve CSS'ten gelen dönüşüm onu geçemiyor. Dokunmatik güvenli:
 *    Motion hover jestinde `pointerType === 'touch'` olaylarını eliyor
 *    (motion-dom/gestures/hover).
 *
 * Sonsuz döngü YOK (bölüm rozeti hariç, aşağıda) — giriş ve hover'ı
 * `<MotionConfig reducedMotion="user">` zaten kapatıyor.
 *
 * Blur yok (giriş imzası olarak `sceneItem` kullanılmıyor): beş öğe × üç
 * parça DESIGN.md §6'daki ~15 öğelik blur bütçesini tek başına doldururdu.
 */

const MODULES = ['analyze', 'workflow', 'agent', 'creative'] as const;
type ModuleKey = (typeof MODULES)[number];

/** Ortadaki panelin görseli. `null` iken yer tutucu çiziliyor. */
const SECTION_SHOT: string | null = null;

/* --- Hareket -------------------------------------------------------------- */

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const lineV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/**
 * Kartın kendi girişi + içindekilerin sırası tek variant'ta. Motion
 * `transition` nesnesindeki orkestrasyon anahtarlarını (`staggerChildren`,
 * `delayChildren`) yayın kendisinden ayırıyor, ikisi bir arada yazılabiliyor.
 *
 * Kartta `hover` variant'ı YOK: yükselen, gölgelenen bir yüzey istemiyoruz
 * (kullanıcı kararı). Hover'da değişen tek şey renk, o da CSS'te.
 */
const cellV: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      ...springFade(spring.default),
      staggerChildren: stagger.base,
      delayChildren: 0.1,
    },
  },
};

/** Kart içindeki tek parça — ikon, başlık, açıklama. */
const partV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/** Ortadaki panelin kabı: çerçeve oturuyor, içindeki görsel onun ardından. */
const shotV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      ...springFade(spring.default),
      staggerChildren: stagger.tight,
      delayChildren: 0.08,
    },
  },
};

/**
 * Çerçevenin İÇİNDEKİ görsel. Kap `overflow: hidden` olduğu için yakınlaşma
 * dışarı taşmıyor ve kaydırma genişliğine dokunmuyor (DESIGN.md §2).
 */
const zoomV: Variants = {
  hidden: { opacity: 0, scale: 1.04 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.default) },
  hover: { scale: 1.04, transition: spring.snap },
};

export function Capabilities() {
  /* İki ad alanı: modül başlıkları menünün anahtarından (tek kaynak), gerisi
     bölümün kendi metinlerinden. */
  const t = useTranslations('capabilities');
  const tMenu = useTranslations('header.menu');
  const uid = useId();

  /* Kartlar önce dizi olarak üretiliyor: ızgaraya ikisi görselden önce,
     ikisi sonra giriyor ve bu ayrım JSX'in içinde okunaklı durmuyordu. */
  const cards = MODULES.map((key, i) => (
    <motion.article
      key={key}
      className={`cap-cell cap-cell--${i + 1}`}
      variants={cellV}
      initial={false}
      animate="show"
    >
      {/* İkon kutusuz, doğrudan zeminde (DESIGN.md §3): kartın en üstünde,
          tek başına. Sıra numarası köşede ve `aria-hidden` — h3 tek başına
          okunuyor, numara sayfanın ızgarasına ait bir işaret. */}
      <motion.span className="cap-card-icon" variants={partV}>
        <ModuleIcon module={key} size={24} />
      </motion.span>
      <span className="cap-card-index" aria-hidden="true">
        {String(i + 1).padStart(2, '0')}
      </span>
      <motion.h3 className="cap-card-title" variants={partV}>
        {tMenu(`${key}.title`)}
      </motion.h3>
      <motion.p className="cap-card-desc" variants={partV}>
        {t(`items.${key}.desc`)}
      </motion.p>
    </motion.article>
  ));

  return (
    <section className="cap" id="modules" aria-labelledby={`${uid}-title`}>
      {/* Dikey hatlar hero'nunkilerin ve SSS'inkilerin tam hizasında:
          .grid-item-8fr `grid-column: 2` ile kendi yerleşiyor. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="cap-inner">
            <motion.div className="cap-head" variants={headV} initial={false} animate="show">
              <motion.p className="cap-eyebrow" variants={lineV}>
                <SectionBadge label={t('eyebrow')} />
              </motion.p>
              <motion.h2 className="cap-title" id={`${uid}-title`} variants={lineV}>
                {t.rich('title', { hl })}
              </motion.h2>
              <motion.p className="cap-lead" variants={lineV}>
                {t('lead')}
              </motion.p>
            </motion.div>

            {/* DOM sırası a → b → görsel → c → d. Tek sütuna düşen telefonda
                yerleşim de bu sırayı çiziyor, o yüzden `order` yok: okuma
                sırası ile görsel sıra hiçbir kademede ayrışmıyor. */}
            <div className="cap-grid">
              {cards.slice(0, 2)}
              <SectionShot />
              {cards.slice(2)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Ortadaki görsel
   ---------------------------------------------------------------------------
   Bölümün tek görseli. Geniş ekranda iki satırı birden kaplıyor; boyu kendi
   oranından değil MADDELERİN boyundan geliyor (ızgaranın `stretch`'i). Dar
   kademelerde 4:5 oranıyla ve genişliği sınırlı duruyor — tam genişlikte
   dikey bir panel telefonda ekranı tek başına dolduruyordu.

   Görsel yokken metin BASILMIYOR ("yakında", "hazırlanıyor" gibi): yer tutucu
   ziyaretçiye eksikliği duyurmamalı, yalnızca yeri tutmalı. O hâlde panel
   ekran okuyucudan da gizli — okunacak bir bilgi yok.

   `loading="lazy"` bilinçli: görsel ilk ekranın çok altında, ilk boyama
   bütçesine girmemeli. `next/image` DEĞİL — tek bir statik varlık;
   iyileştirici katmanın getirisi kurulum maliyetini karşılamıyor.
   --------------------------------------------------------------------------- */

function SectionShot() {
  const t = useTranslations('capabilities');

  return (
    <motion.div
      className="cap-shot"
      variants={shotV}
      initial={false}
      animate="show"
      whileHover="hover"
    >
      {/* Yer tutucunun derisi PAYLAŞILIYOR: aynı ızgara dokusu ve görsel
          işareti blog kartlarının görsel alanında da kullanılıyor
          (components/blog-posts.tsx). Sınıf bu yüzden `.cap-*` ailesinden
          çıkarılıp `.shot-empty` oldu — gerekçe globals.css. */}
      {SECTION_SHOT ? (
        <motion.img
          className="cap-shot-img"
          src={SECTION_SHOT}
          alt={t('shotAlt')}
          loading="lazy"
          decoding="async"
          variants={zoomV}
        />
      ) : (
        <motion.span className="shot-empty" variants={zoomV} aria-hidden="true">
          <RiImageLine size={30} aria-hidden="true" />
        </motion.span>
      )}
    </motion.div>
  );
}

/* ---------------------------------------------------------------------------
   Bölüm rozeti
   ---------------------------------------------------------------------------
   Diğer bölümlerin etiketi düz bir satır; buradaki kap taşıyor, çünkü altında
   dört ayrı modül var ve bölüm bir başlangıç işareti istiyor.

   İki hareket, ikisi de bir şey söylüyor:
   - Kenarda dolaşan ışık (`beam`) — sayfanın 1px'lik ızgara diline ait tek
     hareketli parça; rozeti "açık bir sistem" gibi gösteriyor.
   - Dört kare (`grid`) sırayla yanıyor — aşağıdaki dört modülün ta kendisi.
     Sıra saat yönünde: sol üst → sağ üst → sağ alt → sol alt, yani modüllerin
     2×2 ızgarada okunma sırası.

   İkisi de sonsuz döngü, ikisi de `useReducedMotion()` ile elle kapatılıyor;
   ışık duran hâlde CSS ile gizleniyor.

   🔴 Döngü taşıyan öğeler koşullu BASILMIYOR — `{!reduce && <span/>}` yazmak
   hidrasyon uyuşmazlığı demek: `useReducedMotion()` sunucuda her zaman false,
   istemcide tercih açıksa true, sunucunun bastığı düğüm istemcide bulunmuyor
   ve React bütün ağacı baştan üretiyor. Tercihe göre değişen tek şey `animate`
   prop'u; duran hâlde görünmemesi gereken parçalar CSS'te gizleniyor
   (globals.css → `@media (prefers-reduced-motion: reduce)`).
   --------------------------------------------------------------------------- */

/** Saat yönünde dönüş sırası — DOM sırası (satır satır) ile aynı değil. */
const BADGE_ORDER = [0, 1, 3, 2];

function SectionBadge({ label }: { label: string }) {
  const reduce = useReducedMotion();

  return (
    <span className="cap-badge">
      <span className="cap-badge-beam" aria-hidden="true">
        <motion.span
          className="cap-badge-arc"
          animate={reduce ? undefined : { rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
      </span>

      <span className="cap-badge-grid" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <motion.span
            key={i}
            className="cap-badge-cell"
            animate={reduce ? undefined : { opacity: [0.3, 1, 0.3], scale: [1, 1.35, 1] }}
            transition={{
              duration: 2.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: BADGE_ORDER.indexOf(i) * 0.55,
            }}
          />
        ))}
      </span>

      {label}
    </span>
  );
}

/* ---------------------------------------------------------------------------
   İkonlar — header menüsündekilerle aynı set, aynı eşleşme.
   --------------------------------------------------------------------------- */

function ModuleIcon({ module, size = 20 }: { module: ModuleKey; size?: number }): ReactNode {
  switch (module) {
    case 'analyze':
      return <RiBarChartBoxLine size={size} aria-hidden="true" />;
    case 'workflow':
      return <RiFlowChart size={size} aria-hidden="true" />;
    case 'agent':
      return <RiRobot2Line size={size} aria-hidden="true" />;
    case 'creative':
      return <RiPaletteLine size={size} aria-hidden="true" />;
  }
}
